import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../components/ui/command';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import {
  ArrowLeft, Plus, Loader2, Trash2, CheckCircle2, Camera,
  Download, AlertTriangle, X, Clock, FileText, Mic, MicOff, Check, ChevronsUpDown, Edit2, ZoomIn
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';
import { getFaseColor, FASES_ORDEN, getUnidadCodigo } from './constants';

const EntregaInspeccion = () => {
  const navigate = useNavigate();
  const { id: entregaId } = useParams();
  const [entrega, setEntrega] = useState(null);
  const [inspeccion, setInspeccion] = useState(null);
  const [observaciones, setObservaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // Catalogs
  const [categorias, setCategorias] = useState([]);
  const [lugares, setLugares] = useState([]);
  const [items, setItems] = useState([]);
  const [fallas, setFallas] = useState([]);

  const fileRefs = useRef({});

  // Modal confirmación checklist pendiente
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // 'finalizar' | 'terminar'

  // Carga inicial consolidada (1 sola llamada API)
  const loadInspeccionCompleta = useCallback(async () => {
    try {
      const resp = await api.get(`/entregas/${entregaId}/inspeccion-completa`);
      const d = resp.data;
      setEntrega(d.entrega);
      if (d.inspeccion) {
        setInspeccion(d.inspeccion);
        setObservaciones(d.inspeccion.observaciones || []);
      }
      if (d.catalogos) {
        setCategorias(d.catalogos.categorias || []);
        setLugares(d.catalogos.lugares || []);
        setItems(d.catalogos.items || []);
        setFallas(d.catalogos.fallas || []);
      }
    } catch (err) {
      toast.error('Error al cargar inspección');
      navigate('/entregas');
    } finally {
      setLoading(false);
    }
  }, [entregaId, navigate]);

  // Recargas parciales post-acción
  const loadEntrega = useCallback(async () => {
    try {
      const resp = await api.get(`/entregas/${entregaId}`);
      setEntrega(resp.data);
    } catch (err) {
      toast.error('Error al cargar entrega');
      navigate('/entregas');
    }
  }, [entregaId, navigate]);

  const loadInspeccion = useCallback(async () => {
    try {
      const resp = await api.get(`/entregas/${entregaId}/inspecciones`);
      const inspecciones = resp.data || [];
      const activa = inspecciones.find(i => i.estado === 'en_proceso')
        || inspecciones.find(i => i.estado === 'con_observaciones')
        || inspecciones[0];
      if (activa) {
        const detail = await api.get(`/entregas/inspecciones/${activa.id}`);
        setInspeccion(detail.data);
        setObservaciones(detail.data.observaciones || []);
      }
    } catch (err) {
      // No inspection yet
    }
  }, [entregaId]);

  // Renovación silenciosa de presigned URLs
  useEffect(() => {
    if (!observaciones.length) return;
    // Encontrar la expiración más cercana entre todas las fotos
    let earliestExpiry = null;
    const s3Originals = [];
    for (const obs of observaciones) {
      for (const foto of (obs.fotos || [])) {
        const f = typeof foto === 'string' ? null : foto;
        if (f && f.expires_at) {
          const exp = new Date(f.expires_at).getTime();
          if (!earliestExpiry || exp < earliestExpiry) earliestExpiry = exp;
          s3Originals.push(f.original);
        }
      }
    }
    if (!earliestExpiry || s3Originals.length === 0) return;
    // Renovar 5 minutos antes de la expiración
    const renewIn = Math.max(earliestExpiry - Date.now() - 5 * 60 * 1000, 30000);
    const timer = setTimeout(async () => {
      try {
        const resp = await api.post('/entregas/config/storage/resolve', { urls: s3Originals });
        const resolved = resp.data || {};
        setObservaciones(prev => prev.map(obs => ({
          ...obs,
          fotos: (obs.fotos || []).map(foto => {
            const f = typeof foto === 'string' ? { url: foto, original: foto, expires_at: null } : foto;
            if (f.original && resolved[f.original]) {
              return resolved[f.original];
            }
            return f;
          })
        })));
      } catch (err) {
        // Silencioso: no afecta la UX
      }
    }, renewIn);
    return () => clearTimeout(timer);
  }, [observaciones]);

  useEffect(() => {
    loadInspeccionCompleta();
  }, [loadInspeccionCompleta]);

  const iniciarInspeccion = async () => {
    setSaving(true);
    try {
      const resp = await api.post(`/entregas/${entregaId}/inspecciones`);
      setInspeccion(resp.data);
      setObservaciones([]);
      toast.success('Inspección iniciada');
      loadEntrega();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al iniciar inspección');
    } finally { setSaving(false); }
  };

  const agregarObservacion = async (data) => {
    if (!inspeccion) return null;
    try {
      const resp = await api.post(`/entregas/inspecciones/${inspeccion.id}/observaciones`, data);
      setObservaciones(prev => [...prev, resp.data]);
      setInspeccion(prev => ({...prev, total_observaciones: (prev.total_observaciones || 0) + 1}));
      return resp.data;
    } catch (err) {
      toast.error('Error al agregar observación');
      return null;
    }
  };

  const reloadObservaciones = async () => {
    if (!inspeccion) return;
    try {
      const detail = await api.get(`/entregas/inspecciones/${inspeccion.id}`);
      setObservaciones(detail.data.observaciones || []);
    } catch {}
  };

  const editarObservacion = async (obsId, data) => {
    try {
      await api.put(`/entregas/observaciones/${obsId}`, data);
      toast.success('Observación actualizada');
      await reloadObservaciones();
    } catch (err) {
      toast.error('Error al editar observación');
    }
  };

  const eliminarFoto = async (obsId, fotoUrl) => {
    try {
      await api.delete(`/entregas/inspecciones/fotos`, { params: { foto_url: fotoUrl, obs_id: obsId } });
      toast.success('Foto eliminada');
      await reloadObservaciones();
    } catch (err) {
      toast.error('Error al eliminar foto');
    }
  };

  const eliminarObservacion = async (obsId) => {
    try {
      await api.delete(`/entregas/observaciones/${obsId}`);
      setObservaciones(prev => prev.filter(o => o.id !== obsId));
      setInspeccion(prev => ({...prev, total_observaciones: Math.max(0, (prev.total_observaciones || 0) - 1)}));
      toast.success('Observación eliminada');
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  const subsanarObservacion = async (obsId, notas = '') => {
    try {
      await api.post(`/entregas/observaciones/${obsId}/subsanar`, { notas });
      setObservaciones(prev => prev.map(o => o.id === obsId ? {...o, subsanada: true, fecha_subsanacion: new Date().toISOString()} : o));
      setInspeccion(prev => ({...prev, total_subsanadas: (prev.total_subsanadas || 0) + 1}));
      toast.success('Observación subsanada');
    } catch (err) {
      toast.error('Error al subsanar');
    }
  };

  const uploadFoto = async (obsId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const resp = await api.post(
        `/entregas/inspecciones/${inspeccion.id}/observaciones/${obsId}/fotos`,
        formData, { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setObservaciones(prev => prev.map(o => {
        if (o.id === obsId) {
          const fotoObj = resp.data.foto || { url: resp.data.url, original: resp.data.url, expires_at: null };
          const fotos = [...(o.fotos || []), fotoObj];
          return {...o, fotos};
        }
        return o;
      }));
      toast.success('Foto subida');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir foto');
    }
  };

  const checkChecklistBeforeClose = async (action) => {
    try {
      const resp = await api.get(`/entregas/${entregaId}/checklist-preentrega`);
      const items = resp.data || [];
      const pendientes = items.filter(i => !i.es_conforme);
      if (pendientes.length > 0) {
        setPendingAction(action);
        setShowChecklistModal(true);
        return;
      }
    } catch {
      // Si falla la consulta, continuar sin validación
    }
    if (action === 'finalizar') doFinalizar();
    else doTerminar();
  };

  const doFinalizar = async () => {
    if (!inspeccion) return;
    setSaving(true);
    try {
      const resp = await api.post(`/entregas/inspecciones/${inspeccion.id}/finalizar`);
      if (resp.data.pendientes > 0) {
        toast.info(`Inspección con ${resp.data.pendientes} observaciones pendientes de subsanar`);
      } else {
        toast.success('Inspección terminada sin observaciones');
      }
      navigate(`/entregas/${entregaId}`);
    } catch (err) {
      toast.error('Error al finalizar');
    } finally { setSaving(false); }
  };

  const finalizarInspeccion = () => checkChecklistBeforeClose('finalizar');

  const doTerminar = async () => {
    setSaving(true);
    try {
      await api.post(`/entregas/${entregaId}/transicion`, { nuevo_estado: 'inspeccion_terminada' });
      toast.success('Inspección terminada (con observaciones subsanadas)');
      navigate(`/entregas/${entregaId}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al terminar inspección');
    } finally { setSaving(false); }
  };

  const terminarConObservaciones = () => checkChecklistBeforeClose('terminar');

  const confirmCloseWithPendingChecklist = () => {
    setShowChecklistModal(false);
    if (pendingAction === 'finalizar') doFinalizar();
    else doTerminar();
    setPendingAction(null);
  };

  const descargarPDF = async () => {
    if (!inspeccion || downloadingPdf) return;
    setDownloadingPdf(true);
    try {
      const resp = await api.get(`/entregas/inspecciones/${inspeccion.id}/pdf`, { responseType: 'blob', timeout: 60000 });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Acta_PreEntrega_${entrega?.codigo || 'inspeccion'}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Error al generar PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const isSLAVencida = (obs) => {
    if (obs.subsanada || !obs.sla_vencimiento) return false;
    return new Date() > new Date(obs.sla_vencimiento);
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 size={24} className="animate-spin text-[#9d8272]" /></div>;
  }

  if (!entrega) return null;

  // Modos de la página
  const modoLectura = FASES_ORDEN.indexOf(entrega.fase) > FASES_ORDEN.indexOf('Inspección');
  const modoInspeccion = !modoLectura && inspeccion?.estado === 'en_proceso'; // Registrar observaciones + finalizar
  const modoSubsanacion = !modoLectura && entrega.estado === 'inspeccion_con_observaciones'; // Subsanar + terminar
  const needsInit = !modoLectura && (!inspeccion || (!modoInspeccion && !modoSubsanacion));
  const pendientes = observaciones.filter(o => !o.subsanada).length;
  const subsanadas = observaciones.filter(o => o.subsanada).length;
  const todasSubsanadas = observaciones.length > 0 && pendientes === 0;

  return (
    <div className="space-y-4" data-testid="inspeccion-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/entregas/${entregaId}`)} data-testid="btn-volver-detalle">
            <ArrowLeft size={16} className="mr-1" /> Volver
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-lg font-bold text-[#4E7A79]">{entrega.codigo}</span>
              <Badge style={{ backgroundColor: `${getFaseColor('Inspección')}20`, color: getFaseColor('Inspección') }} variant="outline" className="text-xs">
                Inspección
              </Badge>
            </div>
            <p className="text-sm text-[#888]">{entrega.proyecto_nombre} — {getUnidadCodigo(entrega)}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {inspeccion && (
            <Button variant="outline" onClick={descargarPDF} disabled={downloadingPdf} className="text-xs" data-testid="btn-pdf">
              {downloadingPdf ? <Loader2 size={14} className="animate-spin mr-1" /> : <Download size={14} className="mr-1" />}
              {downloadingPdf ? 'Generando...' : 'PDF'}
            </Button>
          )}
          {/* Modo Inspección: Finalizar */}
          {modoInspeccion && (
            <Button onClick={finalizarInspeccion} disabled={saving} className="bg-[#4E7A79] hover:bg-[#3d6362] text-white" data-testid="btn-finalizar">
              {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : <CheckCircle2 size={14} className="mr-1" />}
              Finalizar Inspección
            </Button>
          )}
          {/* Modo Subsanación: Terminar cuando todas subsanadas */}
          {modoSubsanacion && todasSubsanadas && (
            <Button onClick={terminarConObservaciones} disabled={saving} className="bg-[#4E7A79] hover:bg-[#3d6362] text-white" data-testid="btn-terminar-con-obs">
              {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : <CheckCircle2 size={14} className="mr-1" />}
              Inspección Terminada (Con Obs)
            </Button>
          )}
        </div>
      </div>

      {/* Init: cuando no hay inspección activa y no estamos en subsanación */}
      {needsInit && (
        <Card className="border-2 border-dashed border-[#9d8272]/30">
          <CardContent className="text-center py-8">
            <FileText size={32} className="mx-auto text-[#ccc] mb-2" />
            <p className="text-sm text-[#888] mb-3">
              {!inspeccion ? 'No hay inspección activa para esta entrega.' : `Última inspección: ${inspeccion.estado}`}
            </p>
            <Button onClick={iniciarInspeccion} disabled={saving} className="bg-[#9d8272] hover:bg-[#8a7162] text-white" data-testid="btn-iniciar-inspeccion">
              {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : <Plus size={14} className="mr-1" />}
              {inspeccion ? 'Re-iniciar Inspección' : 'Iniciar Inspección'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Contenido principal: Modo Inspección, Subsanación o Lectura */}
      {(modoInspeccion || modoSubsanacion || modoLectura) && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Main panel: Observations */}
          <div className="lg:col-span-3 space-y-3">
            {/* Banner modo lectura */}
            {modoLectura && (
              <div className="flex items-center gap-2 p-3 bg-slate-100 border border-slate-200 rounded-lg" data-testid="banner-lectura-inspeccion">
                <Clock size={16} className="text-slate-500 shrink-0" />
                <p className="text-sm text-slate-600">
                  <span className="font-medium">Vista histórica</span> — Fase de inspección completada
                </p>
              </div>
            )}
            {/* Banner modo subsanación */}
            {modoSubsanacion && (
              <Card className="border border-orange-200 bg-orange-50/50">
                <CardContent className="py-3 flex items-center gap-2">
                  <AlertTriangle size={16} className="text-orange-500 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-orange-700">Modo Subsanación</p>
                    <p className="text-xs text-orange-600">
                      {todasSubsanadas
                        ? 'Todas las observaciones han sido subsanadas. Puede marcar la inspección como terminada.'
                        : `${pendientes} observación(es) pendiente(s) de subsanar.`}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Formulario nueva observación — solo en modo inspección */}
            {modoInspeccion && (
              <NewObservacionCard
                categorias={categorias} lugares={lugares} items={items} fallas={fallas}
                onSave={agregarObservacion}
                inspeccionId={inspeccion?.id}
                onPhotosUploaded={reloadObservaciones}
              />
            )}

            {/* Lista de observaciones */}
            {observaciones.map(obs => (
              <ObservacionCard
                key={obs.id}
                obs={obs}
                isSLAVencida={isSLAVencida(obs)}
                onDelete={modoInspeccion ? () => eliminarObservacion(obs.id) : null}
                onSubsanar={modoLectura ? null : (notas) => subsanarObservacion(obs.id, notas)}
                onUploadFoto={modoLectura ? null : (file) => uploadFoto(obs.id, file)}
                onDeleteFoto={modoLectura ? null : (fotoUrl) => eliminarFoto(obs.id, fotoUrl)}
                fileRefs={fileRefs}
                readOnly={modoLectura || modoSubsanacion}
                canSubsanar={modoSubsanacion}
                canEdit={modoInspeccion}
                onEdit={modoLectura ? null : (data) => editarObservacion(obs.id, data)}
                categorias={categorias}
                lugares={lugares}
                items={items}
                fallas={fallas}
              />
            ))}

            {observaciones.length === 0 && (
              <div className="text-center py-8 text-sm text-[#888]">
                {modoInspeccion
                  ? 'No hay observaciones registradas. Use el formulario de arriba para agregar.'
                  : 'No hay observaciones en esta inspección.'}
              </div>
            )}
          </div>

          {/* Sidebar: Summary */}
          <div className="lg:col-span-1 space-y-3">
            <Card className="border border-[#E8E8E7]">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-[#5A5A5A]">Resumen</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#2D2D2D]">{observaciones.length}</div>
                  <div className="text-xs text-[#888]">Total observaciones</div>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-yellow-600">Pendientes: {pendientes}</span>
                  <span className="text-green-600">Subsanadas: {subsanadas}</span>
                </div>
                {observaciones.length > 0 && (
                  <div className="w-full bg-[#E8E8E7] rounded-full h-2">
                    <div
                      className="bg-green-500 h-2 rounded-full transition-all"
                      style={{ width: `${observaciones.length > 0 ? (subsanadas / observaciones.length * 100) : 0}%` }}
                    />
                  </div>
                )}
                {observaciones.filter(o => isSLAVencida(o)).length > 0 && (
                  <div className="flex items-center gap-1 text-xs text-red-500">
                    <AlertTriangle size={12} />
                    {observaciones.filter(o => isSLAVencida(o)).length} con SLA vencido
                  </div>
                )}

                {/* Botón terminar con obs en sidebar también */}
                {modoSubsanacion && todasSubsanadas && (
                  <Button onClick={terminarConObservaciones} disabled={saving} className="w-full bg-[#4E7A79] hover:bg-[#3d6362] text-white text-xs mt-2" data-testid="btn-terminar-con-obs-sidebar">
                    {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : <CheckCircle2 size={14} className="mr-1" />}
                    Terminada (Con Obs)
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* By category */}
            {observaciones.length > 0 && (
              <Card className="border border-[#E8E8E7]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-xs font-semibold text-[#5A5A5A]">Por categoría</CardTitle>
                </CardHeader>
                <CardContent className="space-y-1">
                  {Object.entries(
                    observaciones.reduce((acc, o) => { acc[o.categoria_nombre] = (acc[o.categoria_nombre] || 0) + 1; return acc; }, {})
                  ).map(([cat, count]) => (
                    <div key={cat} className="flex justify-between text-xs">
                      <span className="text-[#5A5A5A] truncate">{cat}</span>
                      <span className="font-medium text-[#2D2D2D]">{count}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Modal: Checklist pendiente */}
      <Dialog open={showChecklistModal} onOpenChange={setShowChecklistModal}>
        <DialogContent className="max-w-md" data-testid="dialog-checklist-pendiente">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle size={18} />
              Checklist incompleto
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#5A5A5A]">
            Algunas actividades del checklist aún no han sido completadas. ¿Está seguro de cerrar la fase de Inspección?
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => { setShowChecklistModal(false); setPendingAction(null); }} data-testid="btn-cancelar-cierre">
              Cancelar
            </Button>
            <Button
              onClick={confirmCloseWithPendingChecklist}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="btn-confirmar-cierre-pendiente"
            >
              Sí, cerrar inspección
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};


const NewObservacionCard = ({ categorias, lugares, items, fallas, onSave, inspeccionId, onPhotosUploaded }) => {
  const [form, setForm] = useState({ categoria_nombre: '', lugar_nombre: '', item_nombre: '', falla_nombre: '', descripcion: '' });
  const [fotos, setFotos] = useState([]); // File[]
  const [saving, setSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState('');
  const [zoomPreview, setZoomPreview] = useState(null);
  const cameraRef = useRef(null);
  const fileRef = useRef(null);

  // Voice dictation
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const baseTextRef = useRef('');
  const SpeechRecognition = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);

  const normalizeTranscript = (text) => {
    const lower = text.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  };

  const startListening = () => {
    if (!SpeechRecognition) { toast.error('Tu navegador no soporta dictado por voz'); return; }
    baseTextRef.current = (form.descripcion || '').trim();

    const recognition = new SpeechRecognition();
    recognition.lang = 'es-PE';
    recognition.continuous = false;
    recognition.interimResults = true;

    recognition.onresult = (event) => {
      const result = event.results[0];
      const transcript = result[0].transcript;
      const base = baseTextRef.current;

      if (result.isFinal) {
        const normalized = normalizeTranscript(transcript.trim());
        const newDesc = (base ? base + ' ' : '') + (base ? normalized.charAt(0).toLowerCase() + normalized.slice(1) : normalized);
        baseTextRef.current = newDesc;
        setForm(f => ({ ...f, descripcion: newDesc }));
      } else {
        setForm(f => ({ ...f, descripcion: (base ? base + ' ' : '') + transcript + ' [dictando...]' }));
      }
    };

    recognition.onerror = () => { setListening(false); };
    recognition.onend = () => {
      setListening(false);
      setForm(f => ({ ...f, descripcion: (f.descripcion || '').replace(/\s*\[dictando\.\.\.\]$/, '').trim() }));
    };
    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  };

  const stopListening = () => {
    if (recognitionRef.current) { recognitionRef.current.stop(); }
    setListening(false);
    setForm(f => ({ ...f, descripcion: (f.descripcion || '').replace(/\s*\[dictando\.\.\.\]$/, '').trim() }));
  };

  const addFotoFiles = (files) => {
    const newFiles = Array.from(files).slice(0, 5 - fotos.length);
    setFotos(prev => [...prev, ...newFiles].slice(0, 5));
  };

  const removeFoto = (index) => {
    setFotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!form.categoria_nombre || !form.lugar_nombre || !form.item_nombre || !form.falla_nombre) {
      toast.error('Complete Categoría, Lugar, Ítem y Falla');
      return;
    }
    if (listening) stopListening();
    setSaving(true);
    setSaveProgress('Creando observación...');

    const cat = categorias.find(c => c.nombre === form.categoria_nombre);
    const lug = lugares.find(l => l.nombre === form.lugar_nombre);
    const itm = items.find(i => i.nombre === form.item_nombre);
    const fal = fallas.find(f => f.nombre === form.falla_nombre);

    try {
      const obsResp = await onSave({
        categoria_id: cat?.id, categoria_nombre: form.categoria_nombre,
        lugar_id: lug?.id, lugar_nombre: form.lugar_nombre,
        item_id: itm?.id, item_nombre: form.item_nombre,
        falla_id: fal?.id, falla_nombre: form.falla_nombre,
        descripcion: form.descripcion || undefined
      });

      // Upload fotos if any
      const obsId = obsResp?.id;
      let fotosSubidas = 0;
      if (obsId && fotos.length > 0 && inspeccionId) {
        for (let i = 0; i < fotos.length; i++) {
          setSaveProgress(`Subiendo foto ${i + 1}/${fotos.length}...`);
          try {
            const fd = new FormData();
            fd.append('file', fotos[i]);
            await api.post(`/entregas/inspecciones/${inspeccionId}/observaciones/${obsId}/fotos`, fd, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
            fotosSubidas++;
          } catch (uploadErr) {
            toast.error(`Error al subir foto ${i + 1}: ${uploadErr.response?.data?.detail || 'fallo en almacenamiento'}`);
          }
        }
      }

      setForm({ categoria_nombre: '', lugar_nombre: '', item_nombre: '', falla_nombre: '', descripcion: '' });
      setFotos([]);
      if (fotosSubidas > 0) {
        toast.success(`Observación creada con ${fotosSubidas} foto(s)`);
        if (onPhotosUploaded) onPhotosUploaded();
      } else if (obsResp) {
        toast.success('Observación creada');
      }
    } catch (err) {
      // onSave already handles toast
    } finally {
      setSaving(false);
      setSaveProgress('');
    }
  };

  return (
    <Card className="border-2 border-dashed border-[#4E7A79]/30" data-testid="new-obs-form">
      <CardContent className="pt-4 pb-3 space-y-3">
        <p className="text-xs font-semibold text-[#4E7A79]">Nueva Observación</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <CatCombobox label="Categoría" value={form.categoria_nombre} items={categorias} onChange={v => setForm(f => ({...f, categoria_nombre: v, item_nombre: '', falla_nombre: ''}))} testId="select-obs-categoria" />
          <CatCombobox label="Lugar" value={form.lugar_nombre} items={lugares} onChange={v => setForm(f => ({...f, lugar_nombre: v}))} testId="select-obs-lugar" />
          <CatCombobox label="Ítem" value={form.item_nombre} items={form.categoria_nombre ? items.filter(i => i.categoria_nombre === form.categoria_nombre) : []} onChange={v => setForm(f => ({...f, item_nombre: v}))} testId="select-obs-item" disabled={!form.categoria_nombre} />
          <CatCombobox label="Falla" value={form.falla_nombre} items={form.categoria_nombre ? fallas.filter(f => f.categoria_nombre === form.categoria_nombre) : []} onChange={v => setForm(f => ({...f, falla_nombre: v}))} testId="select-obs-falla" disabled={!form.categoria_nombre} />
        </div>

        {/* Descripción + Dictado por voz */}
        <div className="relative">
          <Textarea
            placeholder="Descripción adicional (opcional) — use el micrófono para dictar"
            value={form.descripcion}
            onChange={e => setForm(f => ({...f, descripcion: e.target.value}))}
            rows={2} className="text-sm pr-12"
            data-testid="input-obs-descripcion"
          />
          {SpeechRecognition && (
            <button
              type="button"
              onClick={listening ? stopListening : startListening}
              className={`absolute right-2 top-2 p-1.5 rounded-full transition-all ${listening ? 'bg-red-100 text-red-600 animate-pulse' : 'bg-[#F5F5F4] text-[#888] hover:text-[#4E7A79] hover:bg-[#4E7A79]/10'}`}
              title={listening ? 'Detener dictado' : 'Dictar por voz'}
              data-testid="btn-dictado-voz"
            >
              {listening ? <MicOff size={16} /> : <Mic size={16} />}
            </button>
          )}
        </div>

        {/* Fotos */}
        <div className="space-y-1.5">
          <span className="text-[10px] text-[#888] font-medium">Evidencia fotográfica ({fotos.length}/5)</span>
          <div className="flex items-start gap-2.5 flex-wrap">
            {fotos.map((file, i) => (
              <div key={i} className="relative group" data-testid={`preview-foto-${i}`}>
                <img
                  src={URL.createObjectURL(file)}
                  alt={`Foto ${i + 1}`}
                  className="w-20 h-20 object-cover rounded-lg border border-[#E8E8E7] shadow-sm cursor-pointer hover:ring-2 hover:ring-[#4E7A79]/50 transition-all"
                  onClick={() => setZoomPreview(URL.createObjectURL(file))}
                />
                <button
                  onClick={() => removeFoto(i)}
                  className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow"
                  data-testid={`btn-remove-foto-${i}`}
                >
                  <X size={12} />
                </button>
              </div>
            ))}
            {fotos.length < 5 && (
              <>
                <button
                  onClick={() => cameraRef.current?.click()}
                  className="w-20 h-20 rounded-lg border-2 border-dashed border-[#4E7A79]/40 flex flex-col items-center justify-center gap-1 hover:border-[#4E7A79] hover:bg-[#4E7A79]/5 transition-all"
                  data-testid="btn-new-obs-camera"
                >
                  <Camera size={20} className="text-[#4E7A79]" />
                  <span className="text-[9px] text-[#4E7A79] font-medium">Cámara</span>
                </button>
                <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
                  onChange={e => { if (e.target.files?.length) { addFotoFiles(e.target.files); e.target.value=''; } }} />
                <button
                  onClick={() => fileRef.current?.click()}
                  className="w-20 h-20 rounded-lg border-2 border-dashed border-[#9d8272]/40 flex flex-col items-center justify-center gap-1 hover:border-[#9d8272] hover:bg-[#9d8272]/5 transition-all"
                  data-testid="btn-new-obs-file"
                >
                  <Plus size={20} className="text-[#9d8272]" />
                  <span className="text-[9px] text-[#9d8272] font-medium">Adjuntar</span>
                </button>
                <input ref={fileRef} type="file" accept="image/*" multiple className="hidden"
                  onChange={e => { if (e.target.files?.length) { addFotoFiles(e.target.files); e.target.value=''; } }} />
              </>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between">
          {saving && saveProgress && (
            <span className="text-xs text-[#4E7A79] flex items-center gap-1">
              <Loader2 size={12} className="animate-spin" /> {saveProgress}
            </span>
          )}
          <div className="flex-1" />
          <Button onClick={handleSave} disabled={saving} size="sm" className="bg-[#4E7A79] hover:bg-[#3d6362] text-white" data-testid="btn-add-obs">
            {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : <Plus size={14} className="mr-1" />}
            Agregar
          </Button>
        </div>
      </CardContent>

      {/* Zoom preview dialog */}
      <Dialog open={!!zoomPreview} onOpenChange={() => setZoomPreview(null)}>
        <DialogContent className="max-w-3xl p-2 bg-black/90 border-none">
          {zoomPreview && (
            <img src={zoomPreview} alt="Zoom" className="w-full max-h-[80vh] object-contain rounded" />
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};


const CatCombobox = ({ label, value, items, onChange, testId, disabled }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="space-y-1">
      <span className="text-[10px] text-[#888]">{label} *</span>
      <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline" role="combobox" aria-expanded={open}
            className={`w-full h-8 justify-between text-xs font-normal px-2 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            data-testid={testId}
            disabled={disabled}
          >
            <span className={`truncate ${!value ? 'text-muted-foreground' : ''}`}>
              {value || label}
            </span>
            <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command>
            <CommandInput placeholder={`Buscar ${label.toLowerCase()}...`} className="h-8 text-xs" />
            <CommandList className="max-h-[200px]">
              <CommandEmpty className="py-3 text-xs">No encontrado.</CommandEmpty>
              <CommandGroup>
                {items.map(i => (
                  <CommandItem
                    key={i.id || i.nombre}
                    value={i.nombre}
                    onSelect={(v) => { onChange(v); setOpen(false); }}
                    className="text-xs"
                  >
                    <Check className={`mr-1.5 h-3 w-3 ${value === i.nombre ? 'opacity-100' : 'opacity-0'}`} />
                    {i.nombre}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};


const ObservacionCard = ({ obs, isSLAVencida: slaVencida, onDelete, onSubsanar, onUploadFoto, onDeleteFoto, fileRefs, readOnly, canSubsanar, canEdit, onEdit, categorias, lugares, items, fallas }) => {
  const cameraRef = useRef(null);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [subsanando, setSubsanando] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [zoomFoto, setZoomFoto] = useState(null);
  const [deletingFoto, setDeletingFoto] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Voice dictation for edit mode
  const [editListening, setEditListening] = useState(false);
  const editRecognitionRef = useRef(null);
  const editBaseTextRef = useRef('');
  const SpeechRecognition = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);

  const normalizeTranscript = (text) => {
    const lower = text.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  };

  const startEditListening = () => {
    if (!SpeechRecognition) { toast.error('Tu navegador no soporta dictado por voz'); return; }
    editBaseTextRef.current = (editForm.descripcion || '').trim();
    const recognition = new SpeechRecognition();
    recognition.lang = 'es-PE';
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.onresult = (event) => {
      const result = event.results[0];
      const transcript = result[0].transcript;
      const base = editBaseTextRef.current;
      if (result.isFinal) {
        const normalized = normalizeTranscript(transcript.trim());
        const newDesc = (base ? base + ' ' : '') + (base ? normalized.charAt(0).toLowerCase() + normalized.slice(1) : normalized);
        editBaseTextRef.current = newDesc;
        setEditForm(f => ({ ...f, descripcion: newDesc }));
      } else {
        setEditForm(f => ({ ...f, descripcion: (base ? base + ' ' : '') + transcript + ' [dictando...]' }));
      }
    };
    recognition.onerror = () => { setEditListening(false); };
    recognition.onend = () => {
      setEditListening(false);
      setEditForm(f => ({ ...f, descripcion: (f.descripcion || '').replace(/\s*\[dictando\.\.\.\]$/, '').trim() }));
    };
    editRecognitionRef.current = recognition;
    recognition.start();
    setEditListening(true);
  };

  const stopEditListening = () => {
    if (editRecognitionRef.current) editRecognitionRef.current.stop();
    setEditListening(false);
    setEditForm(f => ({ ...f, descripcion: (f.descripcion || '').replace(/\s*\[dictando\.\.\.\]$/, '').trim() }));
  };

  const handleUpload = async (file) => {
    setUploading(true);
    try { await onUploadFoto(file); } finally { setUploading(false); }
  };

  const startEdit = () => {
    setEditForm({
      categoria_nombre: obs.categoria_nombre || '',
      lugar_nombre: obs.lugar_nombre || '',
      item_nombre: obs.item_nombre || '',
      falla_nombre: obs.falla_nombre || '',
      descripcion: obs.descripcion || '',
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    if (!editForm.categoria_nombre || !editForm.lugar_nombre || !editForm.item_nombre || !editForm.falla_nombre) {
      toast.error('Complete Categoría, Lugar, Ítem y Falla');
      return;
    }
    setSavingEdit(true);
    const cat = (categorias || []).find(c => c.nombre === editForm.categoria_nombre);
    const lug = (lugares || []).find(l => l.nombre === editForm.lugar_nombre);
    const itm = (items || []).find(i => i.nombre === editForm.item_nombre);
    const fal = (fallas || []).find(f => f.nombre === editForm.falla_nombre);
    await onEdit({
      categoria_id: cat?.id || obs.categoria_id,
      categoria_nombre: editForm.categoria_nombre,
      lugar_id: lug?.id || obs.lugar_id,
      lugar_nombre: editForm.lugar_nombre,
      item_id: itm?.id || obs.item_id,
      item_nombre: editForm.item_nombre,
      falla_id: fal?.id || obs.falla_id,
      falla_nombre: editForm.falla_nombre,
      descripcion: editForm.descripcion || null,
    });
    setSavingEdit(false);
    setEditing(false);
  };

  const fotos = (obs.fotos || []).map(f => typeof f === 'string' ? { url: f, original: f, expires_at: null } : f);
  const canUpload = !obs.subsanada && fotos.length < 5;
  const filteredItems = editForm.categoria_nombre ? (items || []).filter(i => i.categoria_nombre === editForm.categoria_nombre) : [];
  const filteredFallas = editForm.categoria_nombre ? (fallas || []).filter(f => f.categoria_nombre === editForm.categoria_nombre) : [];

  return (
    <Card className={`border ${obs.subsanada ? 'border-green-200 bg-green-50/30' : slaVencida ? 'border-red-200 bg-red-50/30' : 'border-[#E8E8E7]'}`}
          data-testid={`obs-card-${obs.id}`}>
      <CardContent className="pt-3 pb-3 space-y-2">
        {/* Header badges or edit form */}
        {editing ? (
          <div className="space-y-2">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <CatCombobox label="Categoría" value={editForm.categoria_nombre} items={categorias || []} onChange={v => setEditForm(f => ({...f, categoria_nombre: v, item_nombre: '', falla_nombre: ''}))} testId={`edit-cat-${obs.id}`} />
              <CatCombobox label="Lugar" value={editForm.lugar_nombre} items={lugares || []} onChange={v => setEditForm(f => ({...f, lugar_nombre: v}))} testId={`edit-lug-${obs.id}`} />
              <CatCombobox label="Ítem" value={editForm.item_nombre} items={filteredItems} onChange={v => setEditForm(f => ({...f, item_nombre: v}))} testId={`edit-itm-${obs.id}`} disabled={!editForm.categoria_nombre} />
              <CatCombobox label="Falla" value={editForm.falla_nombre} items={filteredFallas} onChange={v => setEditForm(f => ({...f, falla_nombre: v}))} testId={`edit-fal-${obs.id}`} disabled={!editForm.categoria_nombre} />
            </div>
            <div className="relative">
              <Textarea
                value={editForm.descripcion}
                onChange={e => setEditForm(f => ({...f, descripcion: e.target.value}))}
                rows={2} className="text-xs pr-12"
                placeholder="Descripción (opcional) — use el micrófono para dictar"
              />
              {SpeechRecognition && (
                <button
                  type="button"
                  onClick={editListening ? stopEditListening : startEditListening}
                  className={`absolute right-2 top-2 p-1.5 rounded-full transition-all ${editListening ? 'bg-red-100 text-red-600 animate-pulse' : 'bg-[#F5F5F4] text-[#888] hover:text-[#4E7A79] hover:bg-[#4E7A79]/10'}`}
                  title={editListening ? 'Detener dictado' : 'Dictar por voz'}
                  data-testid={`btn-dictado-edit-${obs.id}`}
                >
                  {editListening ? <MicOff size={16} /> : <Mic size={16} />}
                </button>
              )}
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setEditing(false)}>Cancelar</Button>
              <Button size="sm" className="h-7 text-xs bg-[#4E7A79] hover:bg-[#3d6362] text-white" onClick={saveEdit} disabled={savingEdit} data-testid={`btn-save-edit-${obs.id}`}>
                {savingEdit ? <Loader2 size={12} className="animate-spin mr-1" /> : <CheckCircle2 size={12} className="mr-1" />}
                Guardar
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className="text-[10px]">{obs.categoria_nombre}</Badge>
              <Badge variant="outline" className="text-[10px]">{obs.lugar_nombre}</Badge>
              <Badge variant="outline" className="text-[10px]">{obs.item_nombre}</Badge>
              <Badge className="text-[10px] bg-red-100 text-red-700">{obs.falla_nombre}</Badge>
            </div>
            <div className="flex items-center gap-1 shrink-0 ml-2">
              {obs.subsanada ? (
                <Badge className="bg-green-100 text-green-700 text-[10px]"><CheckCircle2 size={10} className="mr-0.5" /> Subsanada</Badge>
              ) : slaVencida ? (
                <Badge className="bg-red-100 text-red-700 text-[10px]"><AlertTriangle size={10} className="mr-0.5" /> SLA Vencida</Badge>
              ) : (
                <Badge className="bg-yellow-100 text-yellow-700 text-[10px]"><Clock size={10} className="mr-0.5" /> Pendiente</Badge>
              )}
            </div>
          </div>
        )}

        {!editing && obs.descripcion && <p className="text-xs text-[#5A5A5A]">{obs.descripcion}</p>}

        {/* SLA countdown */}
        {!editing && !obs.subsanada && obs.sla_vencimiento && (() => {
          const deadline = new Date(obs.sla_vencimiento);
          const now = new Date();
          const diff = deadline - now;
          const vencido = diff <= 0;
          const horas = Math.abs(Math.floor(diff / (1000 * 60 * 60)));
          const mins = Math.abs(Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)));
          const fechaFmt = deadline.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' + deadline.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' });
          return (
            <div className={`flex items-center gap-1.5 text-[11px] ${vencido ? 'text-red-600' : 'text-[#888]'}`} data-testid={`sla-info-${obs.id}`}>
              <Clock size={11} className="shrink-0" />
              <span>Subsanación máx: <strong>{fechaFmt}</strong></span>
              <span className={`ml-1 ${vencido ? 'font-semibold' : ''}`}>
                ({vencido ? `Vencido hace ${horas}h ${mins}m` : `faltan ${horas}h ${mins}m`})
              </span>
            </div>
          );
        })()}

        {/* Photos Section — always visible */}
        <div className="space-y-2">
            {fotos.length > 0 && (
              <div className="flex items-center gap-1 mb-1">
                <Camera size={12} className="text-[#888]" />
                <span className="text-[10px] text-[#888] font-medium">Fotos ({fotos.length}/5)</span>
              </div>
            )}
            <div className="flex items-start gap-2.5 flex-wrap">
              {fotos.map((foto, i) => (
                <div key={i} className="relative group" data-testid={`foto-thumb-${obs.id}-${i}`}>
                  <img src={foto.url} alt={`Foto ${i + 1}`}
                    loading="lazy"
                    className="w-20 h-20 object-cover rounded-lg border border-[#E8E8E7] shadow-sm cursor-pointer hover:shadow-md hover:border-[#9d8272] transition-all"
                    onClick={() => setZoomFoto(foto.url)}
                  />
                  <button
                    onClick={() => setZoomFoto(foto.url)}
                    className="absolute bottom-1 left-1 bg-black/50 text-white rounded p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Ampliar"
                  >
                    <ZoomIn size={12} />
                  </button>
                  {!obs.subsanada && onDeleteFoto && (
                    <button
                      onClick={async () => {
                        setDeletingFoto(foto.original);
                        await onDeleteFoto(foto.original);
                        setDeletingFoto(null);
                      }}
                      disabled={deletingFoto === foto.original}
                      className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow hover:bg-red-600"
                      title="Eliminar foto"
                      data-testid={`btn-delete-foto-${obs.id}-${i}`}
                    >
                      {deletingFoto === foto.original ? <Loader2 size={10} className="animate-spin" /> : <X size={12} />}
                    </button>
                  )}
                </div>
              ))}
              {canUpload && !uploading && (
                <div className="flex gap-2">
                  <button onClick={() => cameraRef.current?.click()}
                    className="w-20 h-20 rounded-lg border-2 border-dashed border-[#4E7A79]/40 flex flex-col items-center justify-center gap-1 hover:border-[#4E7A79] hover:bg-[#4E7A79]/5 transition-all"
                    data-testid={`btn-foto-camera-${obs.id}`}>
                    <Camera size={20} className="text-[#4E7A79]" />
                    <span className="text-[9px] text-[#4E7A79] font-medium">Cámara</span>
                  </button>
                  <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
                    onChange={e => { if (e.target.files?.[0]) { handleUpload(e.target.files[0]); e.target.value=''; } }} />
                  <button onClick={() => fileRef.current?.click()}
                    className="w-20 h-20 rounded-lg border-2 border-dashed border-[#9d8272]/40 flex flex-col items-center justify-center gap-1 hover:border-[#9d8272] hover:bg-[#9d8272]/5 transition-all"
                    data-testid={`btn-foto-file-${obs.id}`}>
                    <Plus size={20} className="text-[#9d8272]" />
                    <span className="text-[9px] text-[#9d8272] font-medium">Adjuntar</span>
                  </button>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden"
                    onChange={e => { if (e.target.files?.[0]) { handleUpload(e.target.files[0]); e.target.value=''; } }} />
                </div>
              )}
              {uploading && (
                <div className="w-20 h-20 rounded-lg border-2 border-dashed border-[#4E7A79]/30 flex flex-col items-center justify-center gap-1 bg-[#4E7A79]/5">
                  <Loader2 size={20} className="animate-spin text-[#4E7A79]" />
                  <span className="text-[9px] text-[#4E7A79]">Subiendo...</span>
                </div>
              )}
              {fotos.length === 0 && !canUpload && (
                <span className="text-[10px] text-[#aaa] italic">Sin fotos</span>
              )}
            </div>
          </div>

        {/* Actions */}
        {!editing && !obs.subsanada && (
          <div className="flex items-center gap-2 pt-1">
            {canSubsanar ? (
              <Button variant="outline" size="sm" className="h-7 text-xs text-green-600" disabled={subsanando} onClick={async () => { setSubsanando(true); try { await onSubsanar(''); } finally { setSubsanando(false); } }} data-testid={`btn-subsanar-${obs.id}`}>
                {subsanando ? <Loader2 size={12} className="animate-spin mr-1" /> : <CheckCircle2 size={12} className="mr-1" />}
                {subsanando ? 'Subsanando...' : 'Subsanar'}
              </Button>
            ) : (
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0} className="inline-flex">
                      <Button variant="outline" size="sm" className="h-7 text-xs text-gray-400 pointer-events-none" disabled data-testid={`btn-subsanar-${obs.id}`}>
                        <CheckCircle2 size={12} className="mr-1" /> Subsanar
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="text-xs">
                    Finalice la inspección para poder subsanar
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {canEdit && !obs.subsanada && (
              <Button variant="outline" size="sm" className="h-7 text-xs text-[#4E7A79]" onClick={startEdit} data-testid={`btn-edit-obs-${obs.id}`}>
                <Edit2 size={12} className="mr-1" /> Editar
              </Button>
            )}
            {onDelete && !readOnly && (
              <Button variant="ghost" size="sm" className="h-7 text-xs text-red-400 hover:text-red-600" onClick={() => setConfirmDelete(true)} data-testid={`btn-delete-obs-${obs.id}`}>
                <Trash2 size={12} className="mr-1" /> Eliminar
              </Button>
            )}

            <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
              <DialogContent className="sm:max-w-[400px]" data-testid={`dialog-confirm-delete-${obs.id}`}>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-sm">
                    <AlertTriangle size={18} className="text-red-500" />
                    Eliminar observación
                  </DialogTitle>
                </DialogHeader>
                <p className="text-sm text-[#666] py-2">¿Está seguro de eliminar esta observación? Esta acción no se puede deshacer.</p>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => setConfirmDelete(false)} data-testid="btn-cancel-delete-obs">
                    Cancelar
                  </Button>
                  <Button size="sm" className="bg-red-500 hover:bg-red-600 text-white" onClick={() => { setConfirmDelete(false); onDelete(); }} data-testid="btn-confirm-delete-obs">
                    Eliminar
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </CardContent>

      {/* Lightbox zoom */}
      <Dialog open={!!zoomFoto} onOpenChange={() => setZoomFoto(null)}>
        <DialogContent className="max-w-3xl p-2 bg-black/95 border-none" data-testid="lightbox-foto">
          {zoomFoto && (
            <img src={zoomFoto} alt="Foto ampliada" className="w-full h-auto max-h-[80vh] object-contain rounded" />
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default EntregaInspeccion;
